import { Component, computed, DestroyRef, HostListener, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AuthService, WorkflowHeaderFacilityFilterService } from '#services';
import { environment } from '#environments';

const GRAPH_PHOTO_URL = 'https://graph.microsoft.com/v1.0/me/photo/$value';
const FALLBACK_AVATAR = '/images/no-avatar.png';
const MAIN_PAGE_URL = '/';
const DEFAULT_FACILITY_NAME = 'OKAN';

@Component({
  selector: 'app-header',
  imports: [],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  private _authService = inject(AuthService);
  private _httpClient = inject(HttpClient);
  private _destroyRef = inject(DestroyRef);
  private _router = inject(Router);
  private _workflowHeaderFacilityFilter = inject(WorkflowHeaderFacilityFilterService);

  private _userDetails = toSignal(this._authService.userDetails$, { initialValue: null });
  private _photoUrl = signal<string | null>(null);
  private _isMainPage = signal<boolean>(false);
  private _facilityMenuOpen = signal<boolean>(false);

  protected fullName = computed(() => this._userDetails()?.username ?? '');
  protected avatarUrl = computed(() => this._photoUrl() ?? FALLBACK_AVATAR);
  protected facilityMenuOpen = this._facilityMenuOpen.asReadonly();
  protected facilities = this._workflowHeaderFacilityFilter.facilities;
  protected selectedFacilityId = this._workflowHeaderFacilityFilter.selectedFacilityId;

  // Hide the badge in production; everywhere else show the env label.
  protected envName = environment.envName;
  protected showEnvBadge = environment.envName !== 'prod';
  protected facilityName = computed(
    () => this._workflowHeaderFacilityFilter.selectedFacility()?.name ?? DEFAULT_FACILITY_NAME
  );
  protected canSelectFacility = computed(
    () => this._isMainPage() && this._workflowHeaderFacilityFilter.facilities().length > 0
  );

  constructor() {
    this.loadProfilePhoto();

    this._isMainPage.set(this.computeIsMainPage());
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this._isMainPage.set(this.computeIsMainPage());
        this._facilityMenuOpen.set(false);
      });

    this._destroyRef.onDestroy(() => {
      const url = this._photoUrl();
      if (url) {
        URL.revokeObjectURL(url);
      }
    });
  }

  protected onFacilityMenuToggle(): void {
    if (!this.canSelectFacility()) {
      return;
    }
    this._facilityMenuOpen.update(open => !open);
  }

  protected onFacilitySelected(facilityId: string): void {
    this._workflowHeaderFacilityFilter.setSelectedFacilityId(facilityId);
    this._facilityMenuOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this._facilityMenuOpen()) {
      return;
    }

    const target = event.target;
    if (target instanceof Element && !target.closest('.header__facility')) {
      this._facilityMenuOpen.set(false);
    }
  }

  private computeIsMainPage(): boolean {
    return this._router.url.split(/[?#]/)[0] === MAIN_PAGE_URL;
  }

  private loadProfilePhoto() {
    this._httpClient
      .get(GRAPH_PHOTO_URL, { responseType: 'blob' })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: blob => this._photoUrl.set(URL.createObjectURL(blob)),
        error: () => this._photoUrl.set(null),
      });
  }
}
